import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

// Minimal typed shim for the beta `supabase.auth.oauth` namespace so this
// route type-checks without grepping SDK internals.
type AuthorizationDetails = {
  client?: { name?: string; logo_uri?: string | null; client_uri?: string | null };
  redirect_uri?: string;
  redirect_url?: string;
  redirect_to?: string;
  scope?: string;
  scopes?: string[];
};
type OAuthResult = { data: AuthorizationDetails | null; error: { message: string } | null };
type SupabaseOAuth = {
  getAuthorizationDetails: (id: string) => Promise<OAuthResult>;
  approveAuthorization: (id: string) => Promise<OAuthResult>;
  denyAuthorization: (id: string) => Promise<OAuthResult>;
};
const authOAuth = (supabase.auth as unknown as { oauth: SupabaseOAuth }).oauth;

type SearchParams = { authorization_id: string };

export const Route = createFileRoute("/.lovable/oauth/consent")({
  // Browser-only: the Supabase client reads its session from localStorage,
  // which is not present during SSR. Without this the loader crashes and
  // signed-in users get bounced to /auth.
  ssr: false,
  validateSearch: (s: Record<string, unknown>): SearchParams => ({
    authorization_id: typeof s.authorization_id === "string" ? s.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      // Preserve the full consent URL so /auth can return the user here with
      // the same authorization_id after sign-in / sign-up / social OAuth.
      const next = location.pathname + location.searchStr;
      throw redirect({ to: "/auth", search: { next } });
    }
  },
  loader: async ({ location }) => {
    const authorizationId = new URLSearchParams(location.search).get("authorization_id")!;
    const { data, error } = await authOAuth.getAuthorizationDetails(authorizationId);
    if (error) throw new Error(error.message);
    // Already-approved client: the provider returns an immediate redirect and
    // no client payload — hop straight to it.
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    return data;
  },
  component: ConsentPage,
  errorComponent: ({ error }) => (
    <div className="grid min-h-screen place-items-center p-4">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>Could not load this authorization request</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {String((error as Error)?.message ?? error)}
          </p>
        </CardContent>
      </Card>
    </div>
  ),
});

function ConsentPage() {
  const details = Route.useLoaderData();
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const { data, error } = approve
      ? await authOAuth.approveAuthorization(authorization_id)
      : await authOAuth.denyAuthorization(authorization_id);
    if (error) {
      setBusy(false);
      setError(error.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "an app";
  const scopes = details?.scopes ?? (details?.scope ? details.scope.split(/\s+/) : []);
  const scopeLabel = (s: string) => {
    if (s === "openid" || s === "profile") return "Share your basic profile";
    if (s === "email") return "Share your email address";
    return `Additional permission requested: ${s}`;
  };

  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="font-serif text-2xl">
            Connect {clientName} to Cerveza.International
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm">
            This lets <strong>{clientName}</strong> use this app as you — it can call the enabled MCP tools while you are signed in.
          </p>

          {details?.redirect_uri && (
            <p className="text-xs text-muted-foreground break-all">
              Callback: <code>{details.redirect_uri}</code>
            </p>
          )}

          {scopes.length > 0 && (
            <ul className="space-y-1 rounded-md border border-border/60 bg-muted/30 p-3 text-sm">
              {scopes.map((s: string) => (
                <li key={s}>• {scopeLabel(s)}</li>
              ))}
            </ul>
          )}

          <p className="text-xs text-muted-foreground">
            This does not bypass Cerveza.International's permissions or backend policies.
          </p>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              disabled={busy}
              onClick={() => decide(false)}
            >
              Cancel connection
            </Button>
            <Button
              className="flex-1"
              disabled={busy}
              onClick={() => decide(true)}
            >
              {busy ? "Working…" : "Approve"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
